import React from 'react';
import type { PayoffTable } from 'utils/shared/quantlib/StrategyBuilder';
import { roundToDecimals } from 'utils/shared/number/roundToDecimals';
import {
  calculateReturnOnMaxRisk,
  maxRiskPercentUnavailableMessage,
  profitPercentUnavailableMessage,
} from 'utils/shared/quantlib/returnOnCost';

export enum TableSetting {
  ProfitAmount = 'PROFIT_AMOUNT',
  ProfitPercent = 'PROFIT_PERCENT',
  ContractValue = 'CONTRACT_VALUE',
  PercentOfMaxRisk = 'PERCENT_OF_MAX_RISK',
  Delta = 'DELTA',
  Gamma = 'GAMMA',
  Theta = 'THETA',
  Vega = 'VEGA',
  Rho = 'RHO',
}

export interface TableDisplay {
  text: string;
  title?: string;
}

export function getTableValue(
  tableSetting: TableSetting,
  data: PayoffTable[0][0],
  maxLoss: number | null | undefined,
): TableDisplay {
  if (tableSetting === TableSetting.ProfitAmount) {
    return { text: new Intl.NumberFormat('en-US').format(Math.round(data.profit)) };
  } else if (tableSetting === TableSetting.ProfitPercent) {
    if (data.profitPercent == null) {
      return { text: 'N/A', title: profitPercentUnavailableMessage(data.profitPercentReason) };
    }
    return {
      text: `${
        Math.abs(data.profitPercent) < 10
          ? data.profitPercent.toFixed(1)
          : new Intl.NumberFormat('en-US').format(Math.round(data.profitPercent))
      }%`,
    };
  } else if (tableSetting === TableSetting.ContractValue) {
    return {
      text: `$${new Intl.NumberFormat('en-US').format(parseFloat(data.contractValue.toFixed(2)))}`,
    };
  } else if (tableSetting === TableSetting.PercentOfMaxRisk) {
    const result = calculateReturnOnMaxRisk({ profit: data.profit, maxLoss });
    return result.percentOfMaxRisk === null
      ? { text: 'N/A', title: maxRiskPercentUnavailableMessage(result.percentOfMaxRiskReason) }
      : { text: `${roundToDecimals(result.percentOfMaxRisk, 0)}%` };
  } else if (tableSetting === TableSetting.Delta) {
    return { text: data.delta.toFixed(1) };
  } else if (tableSetting === TableSetting.Gamma) {
    return { text: data.gamma.toFixed(2) };
  } else if (tableSetting === TableSetting.Theta) {
    return { text: data.theta.toFixed(1) };
  } else if (tableSetting === TableSetting.Vega) {
    return { text: data.vega.toFixed(1) };
  } else if (tableSetting === TableSetting.Rho) {
    return { text: data.rho.toFixed(1) };
  }
  return { text: '' };
}

interface Props {
  display: TableDisplay;
}

/** Shared by the actual OPC cells and render regressions. */
export default function TableValue({ display }: Props) {
  return <span title={display.title}>{display.text}</span>;
}
